import React, { forwardRef } from "react";
import moment from "moment";

const EmployeeSalaryInvoicePrint = forwardRef(({ data }, ref) => {
    if (!data) return null;

    const basicSalary = Number(data.basic_salary || 0);
    const allowancesItems = (data.allowances_list || []).filter(item => item.name.toLowerCase() !== 'basic' && item.name.toLowerCase() !== 'basic salary');
    const allowancesSum = allowancesItems.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    const grossSalary = data.allowances_list && data.allowances_list.length > 0 ? basicSalary + allowancesSum : Number(data.gross_salary || 0);

    const deductionsItems = data.deductions_list || [];
    const deductionsSum = deductionsItems.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    const leaveDeductions = Number(data.total_deductions || 0);
    const totalDeductions = deductionsSum + leaveDeductions;
    const netSalary = grossSalary - totalDeductions;

    return (
        <div
            ref={ref}
            style={{
                padding: "40px",
                fontSize: "12px",
                fontFamily: "Arial, sans-serif",
                color: "#333",
                backgroundColor: "white",
                width: "100%",
                minHeight: "100%"
            }}
        >
            <style>
                {`
                @media print {
                    @page { size: A4; margin: 10mm; }
                    body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                }
                .invoice-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                .invoice-table th, .invoice-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                .invoice-table th { background-color: #f8f9fa; font-weight: bold; }
                .text-right { text-align: right; }
                .text-center { text-align: center; }
                .header-section { margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; display: flex; justify-content: space-between; align-items: flex-end; }
                .section-title { font-size: 14px; font-weight: bold; margin-top: 25px; margin-bottom: 10px; background: #333; color: #fff; padding: 5px 10px; }
                .info-grid { display: flex; flex-wrap: wrap; margin-bottom: 20px; }
                .info-item { width: 50%; margin-bottom: 10px; }
                .info-label { font-weight: bold; display: inline-block; width: 120px; }
                .total-row { background-color: #f0f0f0; font-weight: bold; font-size: 14px; }
                .signature-section { margin-top: 60px; display: flex; justify-content: space-between; }
                .signature-box { text-align: center; width: 200px; }
                .signature-line { border-top: 1px solid #333; margin-bottom: 5px; }
                `}
            </style>

            <div className="header-section">
                <div>
                    <h1 style={{ margin: "0 0 10px 0", fontSize: "24px", textTransform: "uppercase" }}>Salary Slip</h1>
                    <p style={{ margin: 0, fontSize: "14px" }}><strong>Period:</strong> {data.start_date ? moment(data.start_date).format("MMM DD, YYYY") : "N/A"} - {data.end_date ? moment(data.end_date).format("MMM DD, YYYY") : "N/A"}</p>
                </div>
                <div style={{ textAlign: "right" }}>
                    <p style={{ margin: "0 0 5px 0" }}><strong>Date Printed:</strong> {moment().format("MMM DD, YYYY")}</p>
                    <p style={{ margin: 0 }}><strong>Status:</strong> <span style={{ textTransform: "uppercase", fontWeight: "bold" }}>{data.payment_status || "Pending"}</span></p>
                </div>
            </div>

            <div className="section-title">EMPLOYEE DETAILS</div>
            <div className="info-grid">
                <div className="info-item"><span className="info-label">Employee Name:</span> {data.employee_name || "N/A"}</div>
                <div className="info-item"><span className="info-label">Department:</span> {data.department_text || "N/A"}</div>
                <div className="info-item"><span className="info-label">Email:</span> {data.employee_email || "N/A"}</div>
                <div className="info-item"><span className="info-label">Phone:</span> {data.employee_phone || "N/A"}</div>
                <div className="info-item"><span className="info-label">Pay Frequency:</span> <span style={{ textTransform: "capitalize" }}>{data.pay_frequency?.toLowerCase() || "N/A"}</span></div>
                <div className="info-item"><span className="info-label">Working Days:</span> {data.working_days || 0} days</div>
                <div className="info-item"><span className="info-label">Rate Type:</span> <span style={{ textTransform: "capitalize" }}>{data.rate_type?.toLowerCase() || "N/A"}</span></div>
            </div>

            <div style={{ display: "flex", gap: "20px" }}>
                <div style={{ width: "50%" }}>
                    <div className="section-title" style={{ marginTop: 0 }}>EARNINGS</div>
                    <table className="invoice-table">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th className="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Basic Salary</td>
                                <td className="text-right">${basicSalary.toFixed(2)}</td>
                            </tr>
                            {allowancesItems.length > 0 ? (
                                allowancesItems.map((item, idx) => (
                                    <tr key={`al_${idx}`}>
                                        <td>{item.name} {Number(item.percentage) > 0 ? `(${item.percentage}%)` : ''}</td>
                                        <td className="text-right">${Number(item.amount || 0).toFixed(2)}</td>
                                    </tr>
                                ))
                            ) : (
                                data.allowances > 0 && (
                                    <tr>
                                        <td>Allowances</td>
                                        <td className="text-right">${Number(data.allowances || 0).toFixed(2)}</td>
                                    </tr>
                                )
                            )}
                            <tr className="total-row">
                                <td>Gross Earnings</td>
                                <td className="text-right">${grossSalary.toFixed(2)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div style={{ width: "50%" }}>
                    <div className="section-title" style={{ marginTop: 0 }}>DEDUCTIONS</div>
                    <table className="invoice-table">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th className="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deductionsItems.length > 0 && (
                                deductionsItems.map((item, idx) => (
                                    <tr key={`de_${idx}`}>
                                        <td>{item.name} {Number(item.percentage) > 0 ? `(${item.percentage}%)` : ''}</td>
                                        <td className="text-right">-${Number(item.amount || 0).toFixed(2)}</td>
                                    </tr>
                                ))
                            )}
                            <tr>
                                <td>
                                    Leave Deductions
                                    <div style={{ fontSize: "10px", color: "#666" }}>
                                        (Paid: {data.paid_leave_days || 0}, Unpaid: {data.unpaid_leave_days || 0})
                                    </div>
                                </td>
                                <td className="text-right">-${leaveDeductions.toFixed(2)}</td>
                            </tr>
                            <tr className="total-row">
                                <td>Total Deductions</td>
                                <td className="text-right">-${totalDeductions.toFixed(2)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <table className="invoice-table" style={{ marginTop: "20px" }}>
                <tbody>
                    <tr className="total-row" style={{ fontSize: "16px" }}>
                        <td style={{ width: "70%" }}>NET SALARY PAYABLE</td>
                        <td className="text-right" style={{ width: "30%" }}>${netSalary.toFixed(2)}</td>
                    </tr>
                </tbody>
            </table>

            <div className="section-title">PAYMENT DETAILS</div>
            <div className="info-grid">
                <div className="info-item"><span className="info-label">Due Date:</span> {data.payment_due_date ? moment(data.payment_due_date).format("MMM DD, YYYY") : "N/A"}</div>
                <div className="info-item"><span className="info-label">Payment Date:</span> {data.payment_date ? moment(data.payment_date).format("MMM DD, YYYY") : "Not Paid Yet"}</div>
                <div className="info-item"><span className="info-label">Paid By:</span> {data.paid_by || "None"}</div>
                <div className="info-item"><span className="info-label">Payment Method:</span> {data.payment_method ? data.payment_method.replace("_", " ") : "N/A"}</div>
            </div>

            {data.notes && (
                <div style={{ marginTop: "20px", padding: "10px", backgroundColor: "#f9f9f9", border: "1px solid #ddd" }}>
                    <strong>Notes:</strong> {data.notes}
                </div>
            )}

            <div className="signature-section">
                <div className="signature-box">
                    <div className="signature-line"></div>
                    <div>Employee Signature</div>
                </div>
                <div className="signature-box">
                    <div className="signature-line"></div>
                    <div>Authorized Signatory</div>
                </div>
            </div>
        </div>
    );
});

EmployeeSalaryInvoicePrint.displayName = "EmployeeSalaryInvoicePrint";
export default EmployeeSalaryInvoicePrint;
